/**
 * ReinoJuguete · tienda.js
 * Componentes web nativos, sin dependencias. Cargado como módulo (diferido).
 * El HTML funciona sin JavaScript; esto solo mejora la experiencia.
 */

const DATOS = JSON.parse(document.getElementById('datos-tienda')?.textContent || '{}');
const RUTAS = DATOS.rutas || {};
const TEXTOS = DATOS.textos || {};

// Los precios que pinta el JavaScript (cambiar de variante) tienen que salir igual que los que
// pinta Liquid, así que usan el formato de moneda de la tienda. Si no llega, se usa el de España.
const FORMATO_MONEDA = (DATOS.formatoMoneda || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&');
const formatoEspana = new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' });

/** @param {number} centimos @param {string} miles @param {string} decimal @param {number} decimales */
function agruparCifra(centimos, miles, decimal, decimales) {
  const [entera, resto] = (centimos / 100).toFixed(decimales).split('.');
  const conMiles = entera.replace(/\B(?=(\d{3})+(?!\d))/g, miles);
  return resto ? conMiles + decimal + resto : conMiles;
}

/** Formatea céntimos con el formato de la tienda (Ajustes → General → Moneda). */
function formatearPrecio(centimos) {
  if (!FORMATO_MONEDA) return formatoEspana.format(centimos / 100);
  return FORMATO_MONEDA.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, clave) => {
    switch (clave) {
      case 'amount': return agruparCifra(centimos, ',', '.', 2);
      case 'amount_no_decimals': return agruparCifra(centimos, ',', '.', 0);
      case 'amount_with_comma_separator': return agruparCifra(centimos, '.', ',', 2);
      case 'amount_no_decimals_with_comma_separator': return agruparCifra(centimos, '.', ',', 0);
      case 'amount_with_apostrophe_separator': return agruparCifra(centimos, "'", '.', 2);
      case 'amount_with_space_separator': return agruparCifra(centimos, ' ', ',', 2);
      case 'amount_no_decimals_with_space_separator': return agruparCifra(centimos, ' ', ',', 0);
      case 'amount_with_period_and_space_separator': return agruparCifra(centimos, ' ', '.', 2);
      default: return formatoEspana.format(centimos / 100);
    }
  });
}

/** @param {Function} fn @param {number} espera */
function debounce(fn, espera = 250) {
  let temporizador;
  return (...args) => {
    clearTimeout(temporizador);
    temporizador = setTimeout(() => fn(...args), espera);
  };
}

/** Publica un evento propio para analítica (píxeles personalizados de Shopify). */
function analitica(nombre, detalle = {}) {
  try {
    window.Shopify?.analytics?.publish?.(`reinojuguete:${nombre}`, detalle);
  } catch {
    /* La analítica nunca debe romper la tienda. */
  }
}

/** Convierte un fragmento HTML en documento para extraer partes. */
function aDocumento(html) {
  return new DOMParser().parseFromString(html, 'text/html');
}

/* ─── Carrito (API AJAX + Section Rendering API) ─────────────────────────── */
const SECCION_CARRITO = 'carrito-lateral';

const Carrito = {

  /** @param {FormData} datos */
  async anadir(datos) {
    datos.append('sections', SECCION_CARRITO);
    datos.append('sections_url', window.location.pathname);
    const respuesta = await fetch(`${RUTAS.carritoAnadir}.js`, {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: datos,
    });
    const json = await respuesta.json();
    if (!respuesta.ok || json.status) throw new Error(json.description || json.message || TEXTOS.carritoError);
    return json;
  },

  /** @param {number} linea @param {number} cantidad */
  async cambiar(linea, cantidad) {
    const respuesta = await fetch(`${RUTAS.carritoCambiar}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: linea, quantity: cantidad, sections: [SECCION_CARRITO], sections_url: window.location.pathname }),
    });
    const json = await respuesta.json();
    if (!respuesta.ok || json.status) throw new Error(json.description || json.message || TEXTOS.carritoError);
    return json;
  },

};

/* ─── Diálogos: abrir y cerrar con [data-abrir] / [data-cerrar] ──────────── */
function abrirDialogo(id, disparador) {
  const dialogo = document.getElementById(id);
  if (!dialogo || dialogo.open) return;
  dialogo.showModal();
  disparador?.setAttribute('aria-expanded', 'true');
  dialogo.addEventListener('close', () => disparador?.setAttribute('aria-expanded', 'false'), { once: true });
  dialogo.dispatchEvent(new CustomEvent('dialogo:abierto', { bubbles: true }));
}

document.addEventListener('click', (evento) => {
  const abrir = evento.target.closest('[data-abrir]');
  if (abrir) {
    evento.preventDefault();
    abrirDialogo(abrir.dataset.abrir, abrir);
    return;
  }

  const cerrar = evento.target.closest('[data-cerrar]');
  if (cerrar) {
    cerrar.closest('dialog')?.close();
    return;
  }

  // Clic en el fondo oscuro de un diálogo modal: cerrar.
  if (evento.target instanceof HTMLDialogElement && evento.target.open) {
    const caja = evento.target.getBoundingClientRect();
    const dentro = evento.clientX >= caja.left && evento.clientX <= caja.right && evento.clientY >= caja.top && evento.clientY <= caja.bottom;
    if (!dentro) evento.target.close();
  }
});

/* ─── Barra superior: en el móvil los avisos se turnan (con pausa) ──────── */
class BarraAnuncio extends HTMLElement {
  static INTERVALO = 5000;
  #temporizador = null;
  #actual = 0;
  #pausada = false;

  connectedCallback() {
    this.mensajes = [...this.querySelectorAll('[data-mensaje]')];
    if (!this.hasAttribute('data-rotar') || this.mensajes.length < 2) return;
    this.movil = matchMedia('(max-width: 989px)');
    this.boton = this.querySelector('[data-pausa]');
    this.toggleAttribute('data-activa', true);
    this.#mostrar(0);

    this.boton?.addEventListener('click', () => {
      this.#pausada = !this.#pausada;
      this.boton.setAttribute('aria-pressed', String(this.#pausada));
      this.#programar();
    });
    // Quien lee o toca un aviso no debe verlo desaparecer; si el foco llega a uno oculto, se enseña.
    this.addEventListener('focusin', (evento) => {
      const indice = this.mensajes.findIndex((mensaje) => mensaje.contains(evento.target));
      if (indice >= 0) this.#mostrar(indice);
      this.#detener();
    });
    // Al salir el foco, el aviso sigue su turno cuando el foco ya está fuera de la barra.
    this.addEventListener('focusout', () => requestAnimationFrame(() => this.#programar()));
    this.addEventListener('pointerenter', () => this.#detener());
    this.addEventListener('pointerleave', () => this.#programar());
    this.alCambiarVisibilidad = () => this.#programar();
    document.addEventListener('visibilitychange', this.alCambiarVisibilidad);
    this.movil.addEventListener('change', this.alCambiarVisibilidad);
    this.#programar();
  }

  disconnectedCallback() {
    this.#detener();
    if (this.alCambiarVisibilidad) document.removeEventListener('visibilitychange', this.alCambiarVisibilidad);
  }

  #programar() {
    this.#detener();
    if (this.#pausada || !this.movil.matches || document.hidden || this.matches(':focus-within')) return;
    this.#temporizador = setInterval(() => this.#mostrar((this.#actual + 1) % this.mensajes.length), BarraAnuncio.INTERVALO);
  }

  #detener() {
    clearInterval(this.#temporizador);
    this.#temporizador = null;
  }

  /** @param {number} indice */
  #mostrar(indice) {
    this.#actual = indice;
    for (const [n, mensaje] of this.mensajes.entries()) mensaje.toggleAttribute('data-actual', n === indice);
  }
}
customElements.define('barra-anuncio', BarraAnuncio);

/* ─── Cabecera: se oculta al bajar y reaparece al subir; desplegables ────── */
class CabeceraTienda extends HTMLElement {
  #ultimaPosicion = 0;
  #seccion = null;

  connectedCallback() {
    this.#seccion = this.closest('.seccion-cabecera');
    this.#actualizarAltura();
    window.addEventListener('scroll', this.#alDesplazar, { passive: true });
    window.addEventListener('resize', debounce(() => this.#actualizarAltura(), 150));

    this.addEventListener('toggle', this.#alAbrirDesplegable, true);
    if (matchMedia('(hover: hover) and (pointer: fine)').matches) this.#abrirAlPasar();
    document.addEventListener('keydown', (evento) => {
      if (evento.key === 'Escape') this.#cerrarDesplegables();
    });
    document.addEventListener('click', (evento) => {
      if (!this.contains(evento.target)) this.#cerrarDesplegables();
    });

    // El icono del carrito abre el carrito lateral en lugar de navegar.
    this.querySelector('[data-abrir-carrito]')?.addEventListener('click', (evento) => {
      const carrito = document.querySelector('carrito-lateral');
      if (!carrito) return;
      evento.preventDefault();
      carrito.abrir(evento.currentTarget);
    });
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.#alDesplazar);
  }

  #actualizarAltura() {
    const altura = this.#seccion?.offsetHeight || this.offsetHeight;
    document.documentElement.style.setProperty('--altura-cabecera', `${altura}px`);
    if (!this.#seccion?.hasAttribute('data-oculta')) document.documentElement.style.setProperty('--cabecera-visible', `${altura}px`);
  }

  #alDesplazar = () => {
    const posicion = window.scrollY;
    const bajando = posicion > this.#ultimaPosicion;
    const hayAbierto = this.querySelector('details[open]');
    if (this.#seccion && !hayAbierto) {
      const oculta = bajando && posicion > 240;
      this.#seccion.toggleAttribute('data-oculta', oculta);
      document.documentElement.style.setProperty('--cabecera-visible', oculta ? '0px' : `${this.#seccion.offsetHeight}px`);
    }
    this.#ultimaPosicion = posicion;
  };

  /**
   * Con ratón, el desplegable se abre al pasar por encima: con una pequeña espera para no abrirse
   * al cruzar la barra, y otra al salir para no cerrarse camino del panel. El clic y el teclado
   * siguen funcionando igual; si se hace clic en uno abierto al pasar, se queda fijo.
   */
  #abrirAlPasar() {
    for (const detalle of this.querySelectorAll('.cabecera__nav-elemento > details')) {
      const elemento = detalle.parentElement;
      let espera;
      elemento.addEventListener('pointerenter', () => {
        clearTimeout(espera);
        espera = setTimeout(() => {
          if (detalle.open) return;
          detalle.dataset.alPasar = '';
          detalle.open = true;
        }, 90);
      });
      elemento.addEventListener('pointerleave', () => {
        clearTimeout(espera);
        espera = setTimeout(() => {
          if (detalle.hasAttribute('data-al-pasar')) detalle.open = false;
        }, 220);
      });
      detalle.querySelector('summary')?.addEventListener('click', (evento) => {
        if (!detalle.open || !detalle.hasAttribute('data-al-pasar')) return;
        evento.preventDefault();
        delete detalle.dataset.alPasar;
      });
      detalle.addEventListener('toggle', () => {
        if (!detalle.open) delete detalle.dataset.alPasar;
      });
    }
  }

  #alAbrirDesplegable = (evento) => {
    const abierto = evento.target;
    if (!(abierto instanceof HTMLDetailsElement) || !abierto.open) return;
    for (const otro of this.querySelectorAll('details[open]')) if (otro !== abierto) otro.open = false;
  };

  #cerrarDesplegables() {
    for (const detalle of this.querySelectorAll('details[open]')) detalle.open = false;
  }
}
customElements.define('cabecera-tienda', CabeceraTienda);

/* ─── Carrito lateral ────────────────────────────────────────────────────── */
class CarritoLateral extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', this.#alCambiar);
    this.addEventListener('click', this.#alPulsar);
    document.addEventListener('carrito:actualizado', (evento) => this.actualizar(evento.detail.secciones?.[SECCION_CARRITO]));
    this.#vigilarLimites();
  }

  /** Si alguna línea supera el límite de compra de su producto, la deja en el límite y lo anuncia. */
  async #vigilarLimites() {
    const ajuste = this.dialogo && lineaFueraDeLimite(this.dialogo);
    if (!ajuste) return;
    await this.#cambiarLinea(ajuste.linea, ajuste.cantidad);
    this.#anunciar(TEXTOS.limiteAjustado || '');
  }

  get dialogo() {
    return this.querySelector('dialog');
  }

  /** @param {HTMLElement} [disparador] */
  abrir(disparador) {
    if (!this.dialogo.open) this.dialogo.showModal();
    disparador?.setAttribute('aria-expanded', 'true');
    analitica('carrito_abierto');
  }

  /** Sustituye el contenido del diálogo por el HTML recién renderizado, sin cerrarlo. */
  actualizar(html) {
    if (!html) return;
    const nuevo = aDocumento(html).querySelector('dialog[data-carrito]');
    if (!nuevo) return;

    const idFoco = document.activeElement?.id;
    const lineaFoco = document.activeElement?.closest('[data-linea]')?.dataset.linea;
    this.dialogo.innerHTML = nuevo.innerHTML;

    const foco =
      (idFoco && this.dialogo.querySelector(`#${CSS.escape(idFoco)}`)) ||
      (lineaFoco && this.dialogo.querySelector(`[data-linea="${lineaFoco}"] input`)) ||
      this.dialogo.querySelector('[data-cerrar]');
    foco?.focus({ preventScroll: true });

    actualizarContador(Number(nuevo.dataset.cantidad ?? 0), nuevo.dataset.textoContador);
    this.#vigilarLimites();
  }

  async #cambiarLinea(linea, cantidad) {
    const formulario = this.querySelector('#FormularioCarrito');
    formulario?.setAttribute('aria-busy', 'true');
    try {
      const respuesta = await Carrito.cambiar(linea, cantidad);
      this.actualizar(respuesta.sections?.[SECCION_CARRITO]);
      this.#anunciar(TEXTOS.actualizado || '');
    } catch (error) {
      this.#anunciar(error.message || TEXTOS.carritoError);
    } finally {
      formulario?.removeAttribute('aria-busy');
    }
  }

  #alCambiar = (evento) => {
    const campo = evento.target.closest('selector-cantidad[data-linea] input');
    if (!campo) return;
    const linea = Number(campo.closest('selector-cantidad').dataset.linea);
    this.#cambiarLinea(linea, Math.max(0, Number(campo.value) || 0));
  };

  #alPulsar = (evento) => {
    const eliminar = evento.target.closest('[data-eliminar-linea]');
    if (eliminar) this.#cambiarLinea(Number(eliminar.dataset.eliminarLinea), 0);
  };

  #anunciar(texto) {
    const region = this.querySelector('[data-anuncio-carrito]');
    if (region) region.textContent = texto;
  }
}
customElements.define('carrito-lateral', CarritoLateral);

/** Actualiza la burbuja del carrito de la cabecera y su texto accesible. */
function actualizarContador(cantidad, texto) {
  for (const burbuja of document.querySelectorAll('[data-contador-carrito]')) {
    burbuja.textContent = String(cantidad);
    burbuja.hidden = cantidad === 0;
  }
  if (texto) for (const etiqueta of document.querySelectorAll('[data-contador-carrito-texto]')) etiqueta.textContent = texto;
}

/** Balancea la bolsa de la cabecera para confirmar, sin interrumpir, que el producto se ha añadido. */
function balancearBolsa() {
  const bolsa = document.querySelector('[data-bolsa-carrito]');
  if (!bolsa) return;
  bolsa.classList.remove('balanceando');
  void bolsa.offsetWidth; // Reinicia la animación si se añade dos veces seguidas.
  bolsa.classList.add('balanceando');
}

/* ─── Límite de compra por pedido (metacampo ficha.cantidad_maxima) ────── */
/** "1 unidad" o "N unidades", para los mensajes del límite. */
function textoUnidades(cantidad) {
  return cantidad === 1 ? TEXTOS.unaUnidad || '1' : (TEXTOS.nUnidades || '[n]').replace('[n]', String(cantidad));
}

/** Mensaje de límite alcanzado, escrito para el cliente (no es un error técnico). */
function textoLimite(limite) {
  return (TEXTOS.limiteAlcanzado || '').replace('[cantidad]', textoUnidades(limite));
}

/**
 * Comprueba si añadir este formulario supera el límite de compra de su producto, sumando lo que ya
 * hay en el carrito. Devuelve el mensaje para el cliente, o null si se puede añadir.
 */
async function superaLimite(formulario) {
  const contenedor = formulario.closest('[data-cantidad-maxima]');
  if (!contenedor) return null;
  const limite = Number(contenedor.dataset.cantidadMaxima);
  const pedida = Math.max(1, Number(new FormData(formulario).get('quantity')) || 1);
  const carrito = await (await fetch(`${RUTAS.carrito}.js`, { headers: { Accept: 'application/json' } })).json();
  const enCarrito = (carrito.items || [])
    .filter((linea) => String(linea.product_id) === contenedor.dataset.producto)
    .reduce((suma, linea) => suma + linea.quantity, 0);
  return enCarrito + pedida > limite ? textoLimite(limite) : null;
}

/**
 * Busca en el carrito una línea que supere el límite de su producto (sumando todas sus variantes)
 * y devuelve la corrección { linea, cantidad }. Así nadie se lo salta cambiando la cantidad a mano.
 */
function lineaFueraDeLimite(raiz) {
  const porProducto = new Map();
  for (const linea of raiz.querySelectorAll('[data-linea][data-limite]')) {
    const lista = porProducto.get(linea.dataset.producto) ?? [];
    lista.push(linea);
    porProducto.set(linea.dataset.producto, lista);
  }
  for (const lineas of porProducto.values()) {
    const limite = Number(lineas[0].dataset.limite);
    const cantidades = lineas.map((l) => Number(l.querySelector('input')?.value) || 0);
    const total = cantidades.reduce((a, b) => a + b, 0);
    if (total <= limite) continue;
    const ultima = lineas.length - 1;
    return { linea: Number(lineas[ultima].dataset.linea), cantidad: Math.max(0, cantidades[ultima] - (total - limite)) };
  }
  return null;
}

/** Envía un formulario de añadir al carrito y abre el carrito lateral. */
async function enviarAlCarrito(formulario, boton) {
  boton?.setAttribute('aria-busy', 'true');
  boton?.setAttribute('disabled', '');
  try {
    const limite = await superaLimite(formulario);
    if (limite) return { ok: false, mensaje: limite };
    const respuesta = await Carrito.anadir(new FormData(formulario));
    document.dispatchEvent(new CustomEvent('carrito:actualizado', { detail: { secciones: respuesta.sections } }));
    balancearBolsa();
    document.querySelector('carrito-lateral')?.abrir(boton);
    return { ok: true };
  } catch (error) {
    return { ok: false, mensaje: error.message || TEXTOS.carritoError };
  } finally {
    boton?.removeAttribute('aria-busy');
    boton?.removeAttribute('disabled');
  }
}

/* ─── Página del carrito (/cart): cambia la cantidad y recarga ───────────── */
class CarritoPagina extends HTMLElement {
  connectedCallback() {
    const ajuste = lineaFueraDeLimite(this);
    if (ajuste) Carrito.cambiar(ajuste.linea, ajuste.cantidad).then(() => window.location.reload());
    this.addEventListener("change", async (evento) => {
      const campo = evento.target.closest("selector-cantidad[data-linea] input");
      if (!campo) return;
      await Carrito.cambiar(Number(campo.closest("selector-cantidad").dataset.linea), Math.max(0, Number(campo.value) || 0));
      window.location.reload();
    });
    this.addEventListener("click", async (evento) => {
      const boton = evento.target.closest("[data-eliminar-linea]");
      if (!boton) return;
      await Carrito.cambiar(Number(boton.dataset.eliminarLinea), 0);
      window.location.reload();
    });
  }
}
customElements.define("carrito-pagina", CarritoPagina);

/* ─── Añadir rápido (tarjetas y complementos) ────────────────────────────── */
class AnadirRapido extends HTMLElement {
  connectedCallback() {
    this.querySelector('form')?.addEventListener('submit', async (evento) => {
      evento.preventDefault();
      const resultado = await enviarAlCarrito(evento.currentTarget, this.querySelector('[type="submit"]'));
      if (!resultado.ok) window.alert(resultado.mensaje);
    });
  }
}
customElements.define('anadir-rapido', AnadirRapido);

/* ─── Selector de cantidad ───────────────────────────────────────────────── */
class SelectorCantidad extends HTMLElement {
  connectedCallback() {
    // Al escribir a mano, el valor se ajusta antes de que lo lea el carrito (el evento sube después).
    this.addEventListener('change', () => this.#ajustar());
    this.#ajustar();
    this.addEventListener('click', (evento) => {
      const boton = evento.target.closest('[data-paso]');
      if (!boton) return;
      const campo = this.querySelector('input');
      const minimo = Number(campo.min || 0);
      const maximo = Number(campo.max || 99);
      const valor = Math.min(maximo, Math.max(minimo, (Number(campo.value) || 0) + Number(boton.dataset.paso)));
      if (String(valor) === campo.value) return;
      campo.value = String(valor);
      campo.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  /** Mantiene el valor entre el mínimo y el máximo y desactiva el botón + al llegar al máximo. */
  #ajustar() {
    const campo = this.querySelector('input');
    if (!campo) return;
    const minimo = Number(campo.min || 0);
    const maximo = Number(campo.max || 99);
    const valor = Number(campo.value) || 0;
    if (valor > maximo) campo.value = String(maximo);
    if (valor < minimo) campo.value = String(minimo);
    this.querySelector('[data-paso="1"]')?.toggleAttribute('disabled', Number(campo.value) >= maximo);
  }
}
customElements.define('selector-cantidad', SelectorCantidad);

/* ─── Formulario de producto (ficha) ─────────────────────────────────────── */
class FormularioProducto extends HTMLElement {
  connectedCallback() {
    this.formulario = this.querySelector('form');
    this.boton = this.querySelector('[data-boton-anadir]');
    this.error = this.querySelector('[data-error-formulario]');
    this.formulario?.addEventListener('submit', async (evento) => {
      evento.preventDefault();
      if (this.error) this.error.hidden = true;
      const resultado = await enviarAlCarrito(this.formulario, this.boton);
      if (!resultado.ok && this.error) {
        this.error.textContent = resultado.mensaje;
        this.error.hidden = false;
      }
    });
  }
}
customElements.define('formulario-producto', FormularioProducto);

/* ─── Selector de variantes ──────────────────────────────────────────────── */
/** Rehace el bloque de precio para una variante, con la misma estructura que snippets/precio.liquid. */
/**
 * @param {HTMLElement} bloque
 * @param {object} variante
 * @param {{ u: number, p: number } | null} [unidad] - Unidades del pack y precio por unidad (céntimos)
 */
function pintarPrecio(bloque, variante, unidad = null) {
  const elemento = (etiqueta, clase, texto, oculto = false) => {
    const nodo = document.createElement(etiqueta);
    nodo.className = clase;
    nodo.textContent = texto;
    if (oculto) nodo.setAttribute('aria-hidden', 'true');
    return nodo;
  };
  const actual = formatearPrecio(variante.price);
  const rebajado = variante.compare_at_price > variante.price;
  bloque.classList.toggle('precio--rebajado', rebajado);
  const hijos = [];
  if (!rebajado) {
    hijos.push(elemento('span', 'precio__actual', actual));
  } else {
    const anterior = formatearPrecio(variante.compare_at_price);
    const frase = (TEXTOS.precioRebajado || '[precio]').replace('[precio]', actual).replace('[anterior]', anterior);
    hijos.push(
      elemento('span', 'visualmente-oculto', frase),
      elemento('span', 'precio__actual', actual, true),
      elemento('s', 'precio__anterior', anterior, true),
    );
  }
  if (unidad && TEXTOS.precioUnidad) {
    const texto = TEXTOS.precioUnidad.replace('[unidades]', String(unidad.u)).replace('[precio]', formatearPrecio(unidad.p));
    hijos.push(elemento('span', 'precio__unidad', texto));
  }
  bloque.replaceChildren(...hijos);
}

class SelectorVariantes extends HTMLElement {
  connectedCallback() {
    this.variantes = JSON.parse(this.querySelector('[data-variantes]')?.textContent || '[]');
    this.unidades = JSON.parse(this.querySelector('[data-unidades]')?.textContent || '{}');
    this.stockBajo = JSON.parse(this.querySelector('[data-stock-bajo]')?.textContent || '{}');
    this.addEventListener('change', () => this.#alCambiar());
    this.#marcarAgotadas();
  }

  /** Marca las opciones que, con lo ya elegido en las demás, llevan a una variante agotada. */
  #marcarAgotadas() {
    const grupos = [...this.querySelectorAll('fieldset')];
    const elegidas = grupos.map((grupo) => grupo.querySelector('input:checked')?.value);
    for (const [i, grupo] of grupos.entries()) {
      for (const entrada of grupo.querySelectorAll('input')) {
        const opciones = [...elegidas];
        opciones[i] = entrada.value;
        const variante = this.variantes.find((v) => v.options.every((opcion, n) => opcion === opciones[n]));
        const agotada = !variante?.available;
        const etiqueta = this.querySelector(`label[for="${entrada.id}"]`);
        if (!etiqueta) continue;
        etiqueta.classList.toggle('variantes__valor--agotado', agotada);
        let aviso = etiqueta.querySelector('[data-agotada]');
        if (agotada && !aviso) {
          aviso = document.createElement('span');
          aviso.className = 'visualmente-oculto';
          aviso.dataset.agotada = '';
          aviso.textContent = ` (${TEXTOS.agotadoVariante || TEXTOS.agotado})`;
          etiqueta.append(aviso);
        }
        if (!agotada) aviso?.remove();
      }
    }
  }

  /** Pocas unidades de la variante elegida: el aviso solo sale si el dato existe. */
  #pintarStock(seccion, variante) {
    const aviso = seccion.querySelector('[data-aviso-stock]');
    if (!aviso) return;
    const cantidad = variante?.available ? this.stockBajo[variante.id] : null;
    aviso.hidden = !cantidad;
    if (!cantidad) return;
    const texto = cantidad === 1 ? TEXTOS.stockBajoUno : (TEXTOS.stockBajo || '').replace('[cantidad]', String(cantidad));
    const destino = aviso.querySelector('[data-texto-stock]');
    if (destino) destino.textContent = texto;
  }

  #alCambiar() {
    for (const grupo of this.querySelectorAll('fieldset')) {
      const elegido = grupo.querySelector('input:checked')?.value;
      const destino = grupo.querySelector('legend strong');
      if (destino && elegido) destino.textContent = elegido;
    }
    const opciones = [...this.querySelectorAll('fieldset')].map((grupo) => grupo.querySelector('input:checked')?.value);
    const variante = this.variantes.find((v) => v.options.every((opcion, i) => opcion === opciones[i]));
    const seccion = this.closest('[data-seccion-producto]');
    if (!seccion) return;
    this.#marcarAgotadas();
    this.#pintarStock(seccion, variante);

    const boton = seccion.querySelector('[data-boton-anadir]');
    const textoBoton = boton?.querySelector('.boton__texto');
    for (const campo of seccion.querySelectorAll('input[name="id"]')) campo.value = variante?.id ?? '';

    if (!variante) {
      boton?.setAttribute('disabled', '');
      if (textoBoton) textoBoton.textContent = TEXTOS.noDisponible;
      return;
    }

    boton?.toggleAttribute('disabled', !variante.available);
    if (textoBoton) textoBoton.textContent = variante.available ? TEXTOS.anadir : TEXTOS.agotado;

    for (const precio of seccion.querySelectorAll('[data-precio-variante] [data-precio]')) pintarPrecio(precio, variante, this.unidades[variante.id]);
    // La barra fija del móvil también enseña el precio y el botón de la variante elegida.
    const barra = seccion.querySelector('barra-compra-fija');
    const precioBarra = barra?.querySelector('[data-precio-barra]');
    if (precioBarra) precioBarra.textContent = formatearPrecio(variante.price);
    barra?.querySelector('[data-ir-formulario]')?.toggleAttribute('disabled', !variante.available);

    seccion.dispatchEvent(new CustomEvent('variante:cambio', { detail: { variante } }));

    const galeria = seccion.querySelector('galeria-producto');
    galeria?.filtrarPorVariante(variante.id);
    if (variante.featured_media) galeria?.irAMedio(variante.featured_media.id);

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variante.id);
    window.history.replaceState({}, '', url);
  }
}
customElements.define('selector-variantes', SelectorVariantes);

/* ─── Llévate varios y ahorra: tramos de precio por cantidad en la ficha ─── */
class AhorroVarios extends HTMLElement {
  connectedCallback() {
    this.porcentajes = (this.dataset.porcentajes || '0').split(',').map(Number);
    this.campo = document.getElementById(this.dataset.campo);
    this.addEventListener('click', (evento) => {
      const tramo = evento.target.closest('[data-cantidad]');
      if (!tramo || !this.campo) return;
      const maximo = Number(this.campo.max || 99);
      this.campo.value = String(Math.min(maximo, Number(tramo.dataset.cantidad)));
      this.campo.dispatchEvent(new Event('change', { bubbles: true }));
    });
    this.campo?.addEventListener('change', () => this.#marcar());
    this.closest('[data-seccion-producto]')?.addEventListener('variante:cambio', (evento) => this.#pintar(evento.detail.variante));
    this.#marcar();
  }

  #marcar() {
    const cantidad = Math.min(3, Math.max(1, Number(this.campo?.value) || 1));
    for (const tramo of this.querySelectorAll('[data-cantidad]')) tramo.setAttribute('aria-pressed', String(Number(tramo.dataset.cantidad) === cantidad));
  }

  /** Precio por unidad de cada tramo con el precio de la variante; el ahorro se redondea hacia abajo, como en Liquid. */
  #pintar(variante) {
    for (const [i, porcentaje] of this.porcentajes.entries()) {
      const destino = this.querySelector(`[data-precio-tramo="${i + 1}"]`);
      if (!destino) continue;
      const precio = variante.price - Math.floor((variante.price * porcentaje) / 100);
      destino.textContent = (TEXTOS.porUnidad || '[precio]').replace('[precio]', formatearPrecio(precio));
    }
  }
}
customElements.define('ahorro-varios', AhorroVarios);

/* ─── Galería de producto (deslizable, con puntos) ───────────────────────── */
class GaleriaProducto extends HTMLElement {
  connectedCallback() {
    this.pista = this.querySelector('[data-pista]');
    this.puntos = [...this.querySelectorAll('[data-punto]')];
    this.miniaturas = [...this.querySelectorAll('[data-miniatura]')];
    this.medios = [...this.querySelectorAll('[data-medio]')];

    const observador = new IntersectionObserver(
      (entradas) => {
        for (const entrada of entradas) {
          if (entrada.isIntersecting) this.#marcar(this.medios.indexOf(entrada.target));
        }
      },
      { root: this.pista, threshold: 0.6 },
    );
    for (const medio of this.medios) observador.observe(medio);

    for (const control of [...this.puntos, ...this.miniaturas]) {
      control.addEventListener('click', () => this.#ir(Number(control.dataset.indice)));
    }

    // Con fotos por variante, se ven las generales y las de la variante elegida (no las de las otras).
    if (this.dataset.varianteInicial) this.filtrarPorVariante(this.dataset.varianteInicial);

    // Si se entra con una variante en la dirección (?variant=), la galería empieza en su foto.
    if (this.dataset.medioInicial) requestAnimationFrame(() => this.irAMedio(this.dataset.medioInicial, { instantaneo: true }));

    // Si cambia el ancho (giro del móvil, carga tardía de la maqueta), la foto actual vuelve a encajar.
    let anchoAnterior = this.pista.clientWidth;
    new ResizeObserver(() => {
      if (this.pista.clientWidth === anchoAnterior) return;
      anchoAnterior = this.pista.clientWidth;
      const medio = this.medios[this.actual ?? 0];
      if (medio) this.pista.scrollLeft = medio.offsetLeft - this.pista.offsetLeft;
    }).observe(this.pista);
  }

  /**
   * Oculta las fotos que son de otras variantes (las generales no llevan data-de-variantes y se quedan).
   * @param {number|string} idVariante
   */
  filtrarPorVariante(idVariante) {
    if (!this.hasAttribute('data-por-variante')) return;
    const id = String(idVariante);
    const ocultos = new Set();
    for (const medio of this.medios) {
      const de = medio.dataset.deVariantes?.split(' ');
      const oculto = Boolean(de) && !de.includes(id);
      medio.hidden = oculto;
      if (oculto) ocultos.add(medio.dataset.idMedio);
    }
    for (const control of [...this.puntos, ...this.miniaturas]) control.hidden = ocultos.has(control.dataset.idMedio);
    this.#marcar(this.actual ?? 0);
  }

  /**
   * @param {number|string} idMedio
   * @param {{ instantaneo?: boolean }} [opciones]
   */
  irAMedio(idMedio, { instantaneo = false } = {}) {
    const indice = this.medios.findIndex((m) => m.dataset.idMedio === String(idMedio));
    if (indice >= 0) this.#ir(indice, instantaneo);
  }

  #ir(indice, instantaneo = false) {
    const medio = this.medios[indice];
    if (!medio) return;
    const sinAnimacion = instantaneo || matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.pista.scrollTo({ left: medio.offsetLeft - this.pista.offsetLeft, behavior: sinAnimacion ? 'auto' : 'smooth' });
    this.#marcar(indice);
  }

  #marcar(indice) {
    this.actual = indice;
    for (const [i, control] of [...this.puntos.entries(), ...this.miniaturas.entries()]) {
      control.toggleAttribute('aria-current', i === indice);
    }
    // El contador cuenta solo las fotos a la vista.
    const visibles = this.medios.filter((medio) => !medio.hidden);
    const contador = this.querySelector('[data-contador]');
    if (contador) contador.textContent = `${Math.max(visibles.indexOf(this.medios[indice]), 0) + 1} / ${visibles.length}`;
  }
}
customElements.define('galeria-producto', GaleriaProducto);

/* ─── Barra de compra fija (móvil) ───────────────────────────────────────── */
class BarraCompraFija extends HTMLElement {
  connectedCallback() {
    const objetivo = document.querySelector(this.dataset.objetivo);
    if (!objetivo) return;
    new IntersectionObserver(([entrada]) => {
      const pasado = !entrada.isIntersecting && entrada.boundingClientRect.top < 0;
      this.toggleAttribute('data-visible', pasado);
      this.inert = !pasado;
    }).observe(objetivo);
    this.inert = true;

    this.querySelector('[data-ir-formulario]')?.addEventListener('click', () => {
      document.querySelector(this.dataset.formulario)?.requestSubmit();
    });
  }
}
customElements.define('barra-compra-fija', BarraCompraFija);

/* ─── Buscador predictivo ────────────────────────────────────────────────── */
class BuscadorPredictivo extends HTMLElement {
  #controlador = null;

  connectedCallback() {
    this.campo = this.querySelector('input[type="search"]');
    this.destino = this.querySelector('[data-predicciones]');
    this.inicial = this.querySelector('[data-inicial]');
    this.estado = this.querySelector('[data-estado]');
    this.campo?.addEventListener('input', debounce(() => this.#buscar(), 220));
    this.closest('dialog')?.addEventListener('dialogo:abierto', () => this.campo?.focus());
    this.addEventListener('keydown', (evento) => this.#teclado(evento));
  }

  /** Flecha abajo: del campo a los resultados y al siguiente; flecha arriba: al anterior y, desde el primero, al campo. */
  #teclado(evento) {
    if (evento.key !== 'ArrowDown' && evento.key !== 'ArrowUp') return;
    const zona = this.inicial && !this.inicial.hidden ? this.inicial : this.destino;
    const enlaces = [...zona.querySelectorAll('a[href]')].filter((enlace) => enlace.getClientRects().length > 0);
    const actual = enlaces.indexOf(document.activeElement);
    if (!enlaces.length || (actual < 0 && evento.key === 'ArrowUp')) return;
    evento.preventDefault();
    if (evento.key === 'ArrowDown') enlaces[Math.min(actual + 1, enlaces.length - 1)].focus();
    else (actual === 0 ? this.campo : enlaces[actual - 1]).focus();
  }

  async #buscar() {
    const termino = this.campo.value.trim();
    if (termino.length < 2) {
      this.destino.innerHTML = '';
      if (this.inicial) this.inicial.hidden = false;
      return;
    }

    this.#controlador?.abort();
    this.#controlador = new AbortController();
    const url = new URL(RUTAS.busquedaPredictiva, window.location.origin);
    url.searchParams.set('q', termino);
    url.searchParams.set('section_id', this.dataset.seccion);
    url.searchParams.set('resources[type]', 'product,collection,query');
    url.searchParams.set('resources[limit]', '6');
    url.searchParams.set('resources[limit_scope]', 'each');

    try {
      const respuesta = await fetch(url, { signal: this.#controlador.signal });
      if (!respuesta.ok) throw new Error(String(respuesta.status));
      const documento = aDocumento(await respuesta.text());
      const resultados = documento.querySelector('[data-resultados-predictivos]');
      this.destino.innerHTML = resultados?.innerHTML ?? '';
      if (this.inicial) this.inicial.hidden = true;
      if (this.estado) this.estado.textContent = resultados?.dataset.anuncio ?? '';
      analitica('busqueda_predictiva', { termino });
    } catch (error) {
      if (error.name !== 'AbortError') this.destino.innerHTML = '';
    }
  }
}
customElements.define('buscador-predictivo', BuscadorPredictivo);

/* ─── Productos recomendados (carga diferida) ────────────────────────────── */
class ProductosRecomendados extends HTMLElement {
  connectedCallback() {
    new IntersectionObserver(
      async ([entrada], observador) => {
        if (!entrada.isIntersecting) return;
        observador.disconnect();
        const url = new URL(RUTAS.recomendaciones, window.location.origin);
        url.searchParams.set('section_id', this.dataset.seccion);
        url.searchParams.set('product_id', this.dataset.producto);
        url.searchParams.set('limit', this.dataset.limite || '8');
        url.searchParams.set('intent', this.dataset.intencion || 'related');
        try {
          const respuesta = await fetch(url);
          const contenido = aDocumento(await respuesta.text()).querySelector('productos-recomendados');
          if (contenido?.innerHTML.trim()) this.innerHTML = contenido.innerHTML;
          else this.closest('[data-contenedor-recomendados]')?.setAttribute('hidden', '');
        } catch {
          this.closest('[data-contenedor-recomendados]')?.setAttribute('hidden', '');
        }
      },
      { rootMargin: '0px 0px 400px 0px' },
    ).observe(this);
  }
}
customElements.define('productos-recomendados', ProductosRecomendados);

/* ─── Filtros de colección (sin recargar la página) ──────────────────────── */
// Solo se sustituyen las zonas marcadas con [data-actualizable]: el panel de filtros sigue
// abierto y el foco se mantiene mientras el cliente elige.
class FiltrosColeccion extends HTMLElement {
  #controlador = null;

  connectedCallback() {
    const aplicarConRetraso = debounce((formulario) => this.#aplicar(formulario), 350);
    this.addEventListener("change", (evento) => {
      const formulario = evento.target.form;
      if (formulario?.matches("[data-filtros]")) aplicarConRetraso(formulario);
    });
    this.addEventListener("submit", (evento) => {
      if (!evento.target.matches("[data-filtros]")) return;
      evento.preventDefault();
      this.#aplicar(evento.target);
      evento.target.closest("dialog")?.close();
    });
    this.addEventListener("click", (evento) => {
      const enlace = evento.target.closest("a[data-enlace-filtro]");
      if (!enlace) return;
      evento.preventDefault();
      this.#cargar(enlace.href);
      if (enlace.closest(".paginacion")) this.querySelector(".coleccion__resultados")?.scrollIntoView({ block: "start" });
    });
    window.addEventListener("popstate", () => this.#cargar(window.location.href, false));
  }

  #aplicar(formulario) {
    const parametros = new URLSearchParams(new FormData(formulario));
    for (const [clave, valor] of [...parametros.entries()]) if (valor === "") parametros.delete(clave);
    this.#cargar(`${window.location.pathname}?${parametros}`);
  }

  async #cargar(direccion, guardar = true) {
    this.#controlador?.abort();
    this.#controlador = new AbortController();
    const url = new URL(direccion, window.location.origin);
    url.searchParams.set("section_id", this.dataset.seccion);
    this.setAttribute("aria-busy", "true");
    try {
      const respuesta = await fetch(url, { signal: this.#controlador.signal });
      if (!respuesta.ok) throw new Error(String(respuesta.status));
      const nuevo = aDocumento(await respuesta.text());
      const idFoco = document.activeElement?.id;
      for (const zona of this.querySelectorAll("[data-actualizable]")) {
        const reemplazo = nuevo.getElementById(zona.id);
        if (reemplazo) zona.innerHTML = reemplazo.innerHTML;
      }
      if (idFoco) document.getElementById(idFoco)?.focus({ preventScroll: true });
      url.searchParams.delete("section_id");
      if (guardar) window.history.pushState({}, "", url);
      analitica("filtro_aplicado", { busqueda: url.search });
    } catch (error) {
      if (error.name !== "AbortError") window.location.href = direccion;
    } finally {
      this.removeAttribute("aria-busy");
    }
  }
}
customElements.define("filtros-coleccion", FiltrosColeccion);

/* ─── Transición entre páginas: la foto de la tarjeta viaja hasta la ficha ── */
// Solo actúa donde el navegador hace transiciones entre documentos (regla @view-transition de
// base.css). El nombre se pone en el momento del clic para que en la página haya uno solo.
const NOMBRE_FOTO = 'foto-producto';

document.addEventListener('click', (evento) => {
  if (evento.defaultPrevented || evento.button !== 0 || evento.metaKey || evento.ctrlKey || evento.shiftKey || evento.altKey) return;
  const enlace = evento.target.closest('a.tarjeta__enlace, a.torre__enlace');
  if (!enlace) return;
  const foto = enlace.matches('.torre__enlace') ? enlace : enlace.closest('.tarjeta')?.querySelector('.tarjeta__media');
  if (!foto) return;
  for (const otra of document.querySelectorAll('.tarjeta__media, .torre__enlace')) otra.style.viewTransitionName = '';
  foto.style.viewTransitionName = NOMBRE_FOTO;
});

// Al volver atrás, el navegador puede restaurar la página con el nombre puesto: se quita.
window.addEventListener('pageshow', () => {
  for (const foto of document.querySelectorAll('.tarjeta__media, .torre__enlace')) foto.style.viewTransitionName = '';
});

/* ─── Analítica propia: uso del buscador por edad ────────────────────────── */
document.addEventListener('click', (evento) => {
  const chip = evento.target.closest('[data-analitica]');
  if (chip) analitica(chip.dataset.analitica, { destino: chip.getAttribute('href') });
});
